import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { contactLabel, findSimilarContacts, sameContact } from "@/lib/contactSimilarity";

export function SimilarContactsWarning({ matches, onUse, testid }) {
  if (!matches.length) return null;
  return (
    <div data-testid={testid} className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-1">
      {matches.map(({ contact, reason }) => (
        <div key={contact.contact_id} className="flex items-center gap-2">
          <AlertTriangle size={12} className="shrink-0 text-amber-600" />
          <span className="flex-1 min-w-0 truncate">{reason} : <strong>{contactLabel(contact)}</strong></span>
          {onUse && (
            <button type="button" onClick={() => onUse(contact)} data-testid={`${testid}-use-${contact.contact_id}`}
              className="shrink-0 px-2 h-6 rounded bg-amber-600 text-white hover:bg-amber-700 transition-colors">
              Utiliser ce contact
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// Saved-contact selector of a client/prospect. Parent renders the contact fields as children.
export default function ContactPicker({ clientId, value, onApply, autoApplyPrimary = false, testidPrefix = "contact", children }) {
  const [contacts, setContacts] = useState([]);
  const [pick, setPick] = useState("new");
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!clientId) { setContacts([]); setPick("new"); return undefined; }
    let active = true;
    api.get(`/clients/${clientId}/contacts`).then((r) => {
      if (!active) return;
      const list = r.data?.contacts || [];
      setContacts(list);
      const match = list.find((c) => sameContact(c, valueRef.current));
      if (match) setPick(match.contact_id);
      else if (autoApplyPrimary && list.length) { setPick(list[0].contact_id); onApply(list[0]); }
      else setPick("new");
    }).catch(() => active && setContacts([]));
    return () => { active = false; };
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cur = contacts.find((c) => c.contact_id === pick);
    if (cur && !sameContact(cur, value)) setPick("new");
    if (!cur) {
      const match = contacts.find((c) => sameContact(c, value));
      if (match) setPick(match.contact_id);
    }
  }, [value.contact_name, value.contact_email]); // eslint-disable-line react-hooks/exhaustive-deps

  const similar = useMemo(() => (pick === "new" ? findSimilarContacts(contacts, value) : []), [pick, contacts, value]);
  const choose = (id) => {
    setPick(id);
    const c = contacts.find((x) => x.contact_id === id);
    onApply(c || { contact_gender: "", contact_name: "", contact_email: "" });
  };
  const typed = !!(value.contact_name || value.contact_email);

  return (
    <div data-testid={`${testidPrefix}-picker`}>
      {contacts.length > 0 && (
        <div className="mb-2">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Contact enregistré</Label>
          <select data-testid={`${testidPrefix}-pick`} value={pick} onChange={(e) => choose(e.target.value)}
            className="w-full h-10 border border-zinc-200 rounded-md px-2 text-sm bg-white">
            {contacts.map((c) => (
              <option key={c.contact_id} value={c.contact_id}>
                {contactLabel(c)}{c.role ? ` — ${c.role}` : ""}{c.is_primary ? " (principal)" : ""}
              </option>
            ))}
            <option value="new">+ Ajouter un nouveau contact</option>
          </select>
        </div>
      )}
      {children}
      <SimilarContactsWarning matches={similar} testid={`${testidPrefix}-similar-warning`}
        onUse={(c) => { setPick(c.contact_id); onApply(c); }} />
      {clientId && pick === "new" && typed && similar.length === 0 && (
        <p data-testid={`${testidPrefix}-new-hint`} className="text-[10px] text-emerald-700 mt-1">
          Nouveau contact — il sera ajouté à la fiche à l'enregistrement.
        </p>
      )}
    </div>
  );
}
